import { request } from "../../utils/request";

/**
 * 构建创建任务的 FormData
 * 将参数和文件按 multipart/form-data 格式组装
 */
const buildCreateTaskFormData = (params: AiVideo.CreateTaskParams): FormData => {
  const fd = new FormData();
  fd.append("prompt", params.prompt);
  fd.append("generationMode", params.generationMode);
  fd.append("resolution", params.resolution);
  fd.append("duration", String(params.duration));
  if (params.enhancePrompt !== undefined) {
    fd.append("enhancePrompt", String(params.enhancePrompt));
  }
  if (params.aspectRatio) {
    fd.append("aspectRatio", params.aspectRatio);
  }
  if (params.firstFrame) {
    fd.append("firstFrame", params.firstFrame);
  }
  if (params.lastFrame) {
    fd.append("lastFrame", params.lastFrame);
  }
  if (params.referenceImages?.length) {
    params.referenceImages.forEach((file) => fd.append("referenceImages", file));
  }
  if (params.referenceVideos?.length) {
    params.referenceVideos.forEach((file) => fd.append("referenceVideos", file));
  }
  if (params.referenceAudios?.length) {
    params.referenceAudios.forEach((file) => fd.append("referenceAudios", file));
  }
  return fd;
};

/**
 * 从响应中提取列表数据
 * 兼容多种后端返回结构：
 *   - data: T[]
 *   - data: { list: T[] }
 *   - data: { records: T[] }
 *   - data: { rows: T[] }
 *   - data: { items: T[] }
 */
const extractList = <T>(res: AiVideo.ApiListResponse<T> | AiVideo.ApiArrayResponse<T>): T[] => {
  if (!res.data) return [];
  // data: T[]
  if (Array.isArray(res.data)) {
    return res.data;
  }
  // data: { list | records | rows | items: T[] }
  if (typeof res.data === "object") {
    const obj = res.data as Record<string, unknown>;
    const listFields = ["list", "records", "rows", "items"] as const;
    for (const field of listFields) {
      const val = obj[field];
      if (Array.isArray(val)) {
        return val as T[];
      }
    }
  }
  return [];
};

/**
 * 创建 AI 文生视频、图生视频或多模态参考生视频任务
 * POST /ai-video/tasks (multipart/form-data)
 * 返回 AiVideoCreateRespVO：仅 taskId 和初始 status（如 "queued"）
 */
export const createAiVideoTaskApi = (params: AiVideo.CreateTaskParams) =>
  request<AiVideo.ApiObjectResponse<AiVideo.CreateRespVO>>("/ai-video/tasks", {
    method: "POST",
    body: buildCreateTaskFormData(params),
  });

/** 查询 AI 视频作品详情 GET /ai-video/tasks/{id} */
export const getAiVideoTaskDetailApi = (id: number) =>
  request<AiVideo.ApiObjectResponse<AiVideo.TaskRespVO>>(`/ai-video/tasks/${id}`, {
    method: "GET",
  });

/** 我的作品列表；无分页参数返回全部，传 taskId 返回详情 GET /ai-video/tasks */
export const getAiVideoTaskListApi = (taskId?: number) =>
  request<AiVideo.ApiListResponse<AiVideo.TaskRespVO> | AiVideo.ApiArrayResponse<AiVideo.TaskRespVO>>("/ai-video/tasks", {
    method: "GET",
    params: taskId ? { taskId } : undefined,
  });

/** 删除 AI 视频作品 DELETE /ai-video/tasks/{id} */
export const deleteAiVideoTaskApi = (id: number) =>
  request<AiVideo.ApiObjectResponse<null>>(`/ai-video/tasks/${id}`, {
    method: "DELETE",
  });

/**
 * 构建积分预估的 FormData
 * 将参数和参考视频文件按 multipart/form-data 格式组装
 */
const buildPointPreviewFormData = (params: AiVideo.PointPreviewParams): FormData => {
  const fd = new FormData();
  fd.append("resolution", params.resolution);
  fd.append("duration", String(params.duration));
  fd.append("inputImageCount", String(params.inputImageCount));
  if (params.referenceVideos?.length) {
    params.referenceVideos.forEach((file) => fd.append("referenceVideos", file));
  }
  return fd;
};

/** AI 生视频积分预估 POST /ai-video/point-preview (multipart/form-data) */
export const previewAiVideoPointApi = (params: AiVideo.PointPreviewParams) =>
  request<AiVideo.ApiObjectResponse<AiVideo.PointPreviewRespVO>>(
    "/ai-video/point-preview",
    {
      method: "POST",
      body: buildPointPreviewFormData(params),
    },
  );

/** AI 生视频模板列表 GET /ai-video/templates */
export const getAiVideoTemplateListApi = () =>
  request<AiVideo.ApiListResponse<AiVideo.TemplateRespVO> | AiVideo.ApiArrayResponse<AiVideo.TemplateRespVO>>("/ai-video/templates", {
    method: "GET",
  });

/** 查询 AI 生视频模板详情 GET /ai-video/templates/{id} */
export const getAiVideoTemplateDetailApi = (id: number) =>
  request<AiVideo.ApiObjectResponse<AiVideo.TemplateRespVO>>(`/ai-video/templates/${id}`, {
    method: "GET",
  });

/** 导出列表数据提取工具函数，供页面使用 */
export { extractList };
